import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SerifHeading } from '../primitives';
import { useServerFlag } from '../../hooks/useServerFlag';
import styles from './NoPlanBanner.module.css';

/**
 * First-run replacement for the ChampionshipCard. Not the dark shell —
 * a lightweight cream card, per Decisions & Handoff §4b:
 *   "Hide dark banner entirely. Replace with light cream card:
 *    'Got a race on the calendar?' → 'Add a race' / 'Not yet.' Ghost buttons."
 *
 * Distinct from the NoRace variant (step 9) — NoRace keeps the dark
 * shell because the user has a PLAN but no race. NoPlan is the fully
 * empty case where there's no plan at all.
 *
 * UX-S3 fix: both CTAs were inert (no handlers). "Not yet." dismisses
 * the card for the session.
 *
 * 2026-06-24 — goal-race fix: this banner asks "Got a race on the
 * calendar? … the plan sharpens around it", i.e. schedule a FUTURE
 * goal race. "Add a race" now opens the MEET form
 * (`/races?openMeetForm=1`), not the past-results race form (whose
 * date is capped at today and can't hold a future goal race).
 *
 * F-508 — this prompt used to render purely on "no weekly plan", reading a
 * different source than the right-rail race panel (`useUpcomingRace`). It
 * asked an athlete who already has a priority-1 goal race on the calendar to
 * "add a race". `hasRace` (the SAME meets source the rail reads) now
 * suppresses it: with a race present the premise is moot, so the card doesn't
 * render.
 */
export function NoPlanBanner({ hasRace = false }: { hasRace?: boolean }) {
  const [dismissed, setDismissed] = useState(false);
  // Race doors sweep (2026-10-18): "Add a race" is the ATHLETE's door. With `races_2_0_v1` on it opens the
  // Upcoming sheet (`openRaceForm`) for every account; `openMeetForm` is the coach's door on that route, and
  // sends a dual-role account to the team-meet sheet. Above the early return.
  const racesV2 = useServerFlag('races_2_0_v1');
  if (hasRace || dismissed) return null;
  return (
    <section className={styles.banner}>
      <SerifHeading level="sm" as="h3" className={styles.title}>
        Got a race on the calendar?
      </SerifHeading>
      <p className={styles.sub}>Add one and the plan sharpens around it.</p>
      <div className={styles.actions}>
        <Link to={racesV2 ? '/races?openRaceForm=1' : '/races?openMeetForm=1'} className={styles.primary}>
          Add a race
        </Link>
        <button type="button" className={styles.ghost} onClick={() => setDismissed(true)}>
          Not yet.
        </button>
      </div>
    </section>
  );
}
